import { SaveButton, TabContentWrapper } from '@/components/_common';
import {
  InvoiceItemFormValues,
  InvoiceItemUpdateRequest,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import {
  useGetInvoiceNoteItems,
  useUpdateInvoiceNoteItemsId,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import { useEffect, useState } from 'react';
import { PageProps } from '@/types/PageProps';
import { Form, message } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import InvoiceNoteItemsSelectServiceTabs from '../InvoiceNoteItemServiceForm/InvoiceNoteItemsSelectServiceTabs';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import InvoiceNoteItemsSelectPartTabs from '../InvoiceNoteItemsSelectPartForm/InvoiceNoteItemsSelectPartTabs';
import { useGetInvoiceId } from '@/core/domain/invoice/invoice/invoice.hooks';

const initialTabErrorsPart = {
  general: false,
  icms: false,
};

const initialTabErrorsService = {
  general: false,
  values: false,
  iss: false,
};

export default function InvoiceEditNoteItemsSelectPartForm({
  route,
}: PageProps) {
  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;
  const { invoicePartId } = useRouteMatch<{ invoicePartId: string }>().params;

  const intl = useIntl().formatMessage;

  const [tabErrorsPart, setTabErrorsPart] = useState(initialTabErrorsPart);
  const [loadingValidatePart, setLoadingValidatePart] = useState(false);

  const [tabErrorsService, setTabErrorsService] = useState(
    initialTabErrorsService,
  );
  const [loadingValidateService, setLoadingValidateService] = useState(false);

  const [form] = Form.useForm<InvoiceItemFormValues>();

  const { data: invoice } = useGetInvoiceId(Number(invoiceId));

  const updateInvoiceItem = useUpdateInvoiceNoteItemsId();

  const {
    data,
    dataUpdatedAt,
    isLoading: invoiceItemIsLoading,
  } = useGetInvoiceNoteItems(Number(invoicePartId));

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        itemTaxes: {
          ...data.itemTaxes,
          withheld:
            typeof data.itemTaxes?.withheld === 'boolean'
              ? String(data.itemTaxes?.withheld)
              : undefined,
          federalTaxesWithheld:
            typeof data.itemTaxes?.federalTaxesWithheld === 'boolean'
              ? String(data.itemTaxes.federalTaxesWithheld)
              : undefined,
        },
      });
    }
  }, [dataUpdatedAt]);

  const history = useHistory();

  const invoiceType = useLocation().pathname.split('/datas')[0].slice(20, 23);

  function handleTabErrorPart(key: keyof typeof initialTabErrorsPart) {
    setTabErrorsPart((state) => ({ ...state, [key]: true }));
  }

  function handleTabErrorService(key: keyof typeof initialTabErrorsService) {
    setTabErrorsService((state) => ({ ...state, [key]: true }));
  }

  async function handleValidateFieldsPart() {
    try {
      const general = await form
        .validateFields([
          'description',
          'unitMeasurement',
          'ncmId',
          'cfopId',
          'quantity',
          'valueUnitary',
        ])
        .catch(() => handleTabErrorPart('general'));

      const icms = await form
        .validateFields([
          ['itemTaxes', 'icmsCst'],
          ['itemTaxes', 'icmsOrigin'],
        ])
        .catch(() => handleTabErrorPart('icms'));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasDataValues = (data: any) =>
        Object.values(data).every((value) => !!value);

      if (
        !general ||
        !hasDataValues(general) ||
        !icms ||
        !hasDataValues(icms)
      ) {
        message.error('Preencha todos os campos');
        return false;
      } else {
        return true;
      }
    } catch (error) {}
  }

  async function handleValidateFieldsService() {
    try {
      const general = await form
        .validateFields(['description', 'unitMeasurement', 'valueUnitary'])
        .catch(() => handleTabErrorService('general'));

      const values = await form
        .validateFields(['grossServiceValue'])
        .catch(() => handleTabErrorService('values'));

      const iss = await form
        .validateFields([['itemTaxes', 'rate']])
        .catch(() => handleTabErrorService('iss'));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasDataValues = (data: any) =>
        Object.values(data).every((value) => !!value);

      if (
        !general ||
        !hasDataValues(general) ||
        !values ||
        !hasDataValues(values) ||
        !iss ||
        !hasDataValues(iss)
      ) {
        message.error('Preencha todos os campos');
        return false;
      } else {
        return true;
      }
    } catch (error) {}
  }

  const handleSubmit = async (redirect = true) => {
    const formValues = await form.validateFields();

    const obj: InvoiceItemUpdateRequest = {
      ...data,
      ...formValues,
      itemTaxes: {
        ...data?.itemTaxes,
        ...formValues.itemTaxes,
        withheld: formValues.itemTaxes.withheld
          ? Boolean(JSON.parse(formValues.itemTaxes.withheld))
          : undefined,

        federalTaxesWithheld: formValues.itemTaxes.federalTaxesWithheld
          ? Boolean(JSON.parse(formValues.itemTaxes.federalTaxesWithheld))
          : undefined,
      },
      invoiceId: Number(invoiceId),
    };

    await updateInvoiceItem.mutateAsync(obj);

    if (redirect) {
      history.push(`/app/fiscal/invoice/${invoiceType}/${invoiceId}/noteItens`);
    }
  };

  async function onSubmitPart() {
    setLoadingValidatePart(true);
    try {
      setTabErrorsPart(initialTabErrorsPart);

      const allFieldsFilled = await handleValidateFieldsPart();

      if (allFieldsFilled) {
        handleSubmit();
      }
    } catch {
      message.error('Há campos a serem validados!');
    } finally {
      setLoadingValidatePart(false);
    }
  }

  async function onSubmitService() {
    setLoadingValidateService(true);
    try {
      setTabErrorsService(initialTabErrorsService);

      const allFieldsFilled = await handleValidateFieldsService();

      if (allFieldsFilled) {
        handleSubmit();
      }
    } catch {
      message.error('Há campos a serem validados!');
    } finally {
      setLoadingValidateService(false);
    }
  }

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SaveButton
          type="primary"
          onClick={
            invoiceType === InvoiceType.NFS ? onSubmitService : onSubmitPart
          }
          loading={
            updateInvoiceItem.isLoading ||
            loadingValidatePart ||
            loadingValidateService
          }
        >
          {intl({ id: 'commons.entity.save' })}
        </SaveButton>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      {invoiceType === InvoiceType.NFS ? (
        <InvoiceNoteItemsSelectServiceTabs
          form={form}
          handleValidateFieldsService={handleValidateFieldsService}
          handleSubmit={handleSubmit}
          itemId={data?.id}
          tabErrors={tabErrorsService}
          loading={invoiceItemIsLoading || updateInvoiceItem.isLoading}
        />
      ) : (
        <InvoiceNoteItemsSelectPartTabs
          form={form}
          handleValidateFieldsPart={handleValidateFieldsPart}
          handleSubmit={handleSubmit}
          invoiceId={Number(invoiceId)}
          invoiceType={invoiceType}
          itemId={data?.id}
          tabErrors={tabErrorsPart}
          invoice={invoice}
          loading={invoiceItemIsLoading || updateInvoiceItem.isLoading}
        />
      )}
    </TabContentWrapper>
  );
}
